import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { NewsDto, NewsService } from '@millionshow/api-client';
import { Icon } from '@millionshow/client-libs';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import PaginationComponent from '../../../components/pagination/pagination.component';
import { NotFoundComponent } from '../../../components/not-found/not-found.component';
import { SeoService } from '../../../services/seo.service';

@Component({
  selector: 'app-news',
  imports: [CommonModule, Icon, PaginationComponent, NotFoundComponent],
  templateUrl: './news.page.html',
})
export default class NewsPage {
  private readonly newsService = inject<NewsService>(NewsService);
  private readonly seoService = inject(SeoService);

  query = signal<string>('');
  pageNo = signal<number>(1);
  pageSize = signal<number>(8);

  private searchSubject = new Subject<string>();

  constructor() {
    this.seoService.set({
      title: '보도자료',
      description: '밀리언쇼 관련 보도자료와 언론 보도를 확인해보세요.',
      path: '/gallery/news',
    });

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(searchQuery => {
        this.query.set(searchQuery);
        this.pageNo.set(1);
      });
  }

  $newsData = rxResource({
    stream: ({ params }) => {
      return this.newsService.newsControllerSearchV1({
        ...params,
        isExposed: true,
      });
    },
    params: () => ({
      pageSize: this.pageSize(),
      query: this.query(),
      type: 'MILLIONSHOW' as NewsDto['type'],
    }),
  });

  newsList = computed(() => this.$newsData.value()?.items || []);

  totalCount = computed(() => {
    return this.$newsData.value()?.pageInfo?.totalItems || 0;
  });

  currentPage = computed(() => this.pageNo());

  totalPages = computed<number[]>(() => {
    const totalItems = this.$newsData.value()?.pageInfo?.totalItems || 0;
    const count = Math.max(1, Math.ceil(totalItems / this.pageSize()));
    return Array.from({ length: count }, (_, i) => i + 1);
  });

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  onPageChange(page: number): void {
    this.pageNo.set(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  }
}
